import { User } from '../types/user.types';

const WEIGHTS = {
  budget: 25,
  lifestyle: 25,
  sleepSchedule: 10,
  cleanliness: 10,
  interests: 15,
  dailyRhythms: 8, // Shared living rhythm / vibe overlap
  smoking: 5,
  pets: 5,
  province: 3,
  metro: 2,
  city: 1,        // Reduced from 3 (legacy fallback)
  neighborhoods: 4, // Increased from 2
} as const;

type WeightKey = keyof typeof WEIGHTS;

export interface CompatibilityBreakdown {
  /** Final, normalized score from 0–100 (rounded). */
  score: number;
  /** Raw weighted score before normalization. */
  rawScore: number;
  /** Total effective weight used for normalization. */
  totalWeight: number;
  /** Per-dimension contribution (already weighted, not normalized). */
  components: Record<WeightKey, number>;
}

/**
 * Backwards-compatible: same API as before.
 * Returns only the final 0–100 compatibility score.
 */
export function calculateCompatibilityScore(userA: User, userB: User): number {
  const result = calculateCompatibilityWithBreakdown(userA, userB);
  return result.score;
}

/**
 * Extended version with a breakdown useful for debugging, tuning or UI.
 */
export function calculateCompatibilityWithBreakdown(
  userA: User,
  userB: User
): CompatibilityBreakdown {
  let score = 0;
  let totalWeight = 0;

  const components: Record<WeightKey, number> = {
    budget: 0,
    lifestyle: 0,
    sleepSchedule: 0,
    cleanliness: 0,
    interests: 0,
    dailyRhythms: 0,
    smoking: 0,
    pets: 0,
    province: 0,
    metro: 0,
    city: 0,
    neighborhoods: 0,
  };

  // ─────────────────────────────────────────────
  // 1. Budget Overlap (25% weight) - Critical
  // ─────────────────────────────────────────────
  // Validate budget values
  if (!userA.budgetMin || !userA.budgetMax || !userB.budgetMin || !userB.budgetMax) {
    // Missing budget data → no match
    return {
      score: 0,
      rawScore: 0,
      totalWeight: WEIGHTS.budget,
      components,
    };
  }
  
  // Normalize inverted budgets (min > max)
  const aBudgetMin = Math.min(userA.budgetMin, userA.budgetMax);
  const aBudgetMax = Math.max(userA.budgetMin, userA.budgetMax);
  const bBudgetMin = Math.min(userB.budgetMin, userB.budgetMax);
  const bBudgetMax = Math.max(userB.budgetMin, userB.budgetMax);
  
  const budgetOverlap = calculateBudgetOverlapNormalized(
    aBudgetMin, aBudgetMax,
    bBudgetMin, bBudgetMax
  );

  if (budgetOverlap <= 0) {
    // Preserve original behavior: no overlap → no match.
    return {
      score: 0,
      rawScore: 0,
      totalWeight: WEIGHTS.budget,
      components,
    };
  }

  const rangeA = aBudgetMax - aBudgetMin;
  const rangeB = bBudgetMax - bBudgetMin;

  const avgBudgetRange = (rangeA + rangeB) / 2;
  const overlapRatio =
    avgBudgetRange > 0 ? Math.min(budgetOverlap / avgBudgetRange, 1) : 0;

  const budgetScore = WEIGHTS.budget * (0.7 + 0.3 * overlapRatio); // base 70% + bonus
  score += budgetScore;
  totalWeight += WEIGHTS.budget;
  components.budget = budgetScore;

  // ─────────────────────────────────────────────
  // 2. Lifestyle Match (25% weight)
  // ─────────────────────────────────────────────
  const lifestyleMatch = calculateArrayMatch(
    userA.lifestyle ?? [],
    userB.lifestyle ?? []
  );
  const lifestyleScore = lifestyleMatch * WEIGHTS.lifestyle;
  score += lifestyleScore;
  totalWeight += WEIGHTS.lifestyle;
  components.lifestyle = lifestyleScore;

  // ─────────────────────────────────────────────
  // 3. Sleep Schedule (10% weight) - optional
  // ─────────────────────────────────────────────
  if (userA.sleepSchedule && userB.sleepSchedule) {
    let sleepScore = 0;

    if (userA.sleepSchedule === userB.sleepSchedule) {
      sleepScore = WEIGHTS.sleepSchedule;
    } else if (
      userA.sleepSchedule === 'flexible' ||
      userB.sleepSchedule === 'flexible'
    ) {
      sleepScore = WEIGHTS.sleepSchedule * 0.5; // Partial match
    }

    score += sleepScore;
    totalWeight += WEIGHTS.sleepSchedule;
    components.sleepSchedule = sleepScore;
  }

  // ─────────────────────────────────────────────
  // 4. Cleanliness (10% weight) - optional
  // ─────────────────────────────────────────────
  if (userA.cleanliness && userB.cleanliness) {
    let cleanlinessScore = 0;

    if (userA.cleanliness === userB.cleanliness) {
      cleanlinessScore = WEIGHTS.cleanliness;
    } else if (
      userA.cleanliness === 'balanced' ||
      userB.cleanliness === 'balanced'
    ) {
      cleanlinessScore = WEIGHTS.cleanliness * 0.5; // Partial match
    }

    score += cleanlinessScore;
    totalWeight += WEIGHTS.cleanliness;
    components.cleanliness = cleanlinessScore;
  }

  // ─────────────────────────────────────────────
  // 5. Interests (15% weight)
  // ─────────────────────────────────────────────
  const interestsMatch = calculateArrayMatch(
    userA.interests ?? [],
    userB.interests ?? []
  );
  const interestsScore = interestsMatch * WEIGHTS.interests;
  score += interestsScore;
  totalWeight += WEIGHTS.interests;
  components.interests = interestsScore;

  // ─────────────────────────────────────────────
  // 5b. Daily rhythms / vibe (8% weight) - optional
  // ─────────────────────────────────────────────
  if (
    userA.dailyRhythms &&
    userB.dailyRhythms &&
    userA.dailyRhythms.length > 0 &&
    userB.dailyRhythms.length > 0
  ) {
    const rhythmMatch = calculateArrayMatch(
      userA.dailyRhythms,
      userB.dailyRhythms
    );
    const rhythmScore = rhythmMatch * WEIGHTS.dailyRhythms;
    score += rhythmScore;
    totalWeight += WEIGHTS.dailyRhythms;
    components.dailyRhythms = rhythmScore;
  }

  // ─────────────────────────────────────────────
  // 6. Smoking (5% weight) - optional
  // ─────────────────────────────────────────────
  if (userA.smokingPreference && userB.smokingPreference) {
    if (areCompatible(userA.smokingPreference, userB.smokingPreference)) {
      score += WEIGHTS.smoking;
      components.smoking = WEIGHTS.smoking;
    }
    // Only count weight when both users have defined preferences
    totalWeight += WEIGHTS.smoking;
  }

  // ─────────────────────────────────────────────
  // 7. Pets (5% weight) - optional
  // ─────────────────────────────────────────────
  if (userA.petsPreference && userB.petsPreference) {
    if (areCompatible(userA.petsPreference, userB.petsPreference)) {
      score += WEIGHTS.pets;
      components.pets = WEIGHTS.pets;
    }
    // Only count weight when both users have defined preferences
    totalWeight += WEIGHTS.pets;
  }

  // ─────────────────────────────────────────────
  // 8. Province (3% weight) - optional
  // ─────────────────────────────────────────────
  if (userA.provinceCode && userB.provinceCode) {
    if (userA.provinceCode === userB.provinceCode) {
      score += WEIGHTS.province;
      components.province = WEIGHTS.province;
    }
    totalWeight += WEIGHTS.province;
  }

  // ─────────────────────────────────────────────
  // 9. Metro/City (2% weight) - optional
  // ─────────────────────────────────────────────
  if (userA.metro && userB.metro) {
    if (userA.metro === userB.metro) {
      score += WEIGHTS.metro;
      components.metro = WEIGHTS.metro;
    }
    totalWeight += WEIGHTS.metro;
  }

  // ─────────────────────────────────────────────
  // 10. City (1% weight) - legacy fallback
  // ─────────────────────────────────────────────
  if (userA.city && userB.city && !userA.metro && !userB.metro) {
    const cityA = userA.city.toLowerCase().trim();
    const cityB = userB.city.toLowerCase().trim();

    if (cityA === cityB) {
      score += WEIGHTS.city;
      components.city = WEIGHTS.city;
    }

    // Only count city weight when both cities are known
    totalWeight += WEIGHTS.city;
  }

  // ─────────────────────────────────────────────
  // 11. Neighborhoods (4% weight) - optional
  // ─────────────────────────────────────────────
  if (
    userA.neighborhoods &&
    userB.neighborhoods &&
    userA.neighborhoods.length > 0 &&
    userB.neighborhoods.length > 0
  ) {
    const neighborhoodMatch = calculateArrayMatch(
      userA.neighborhoods,
      userB.neighborhoods
    );
    const neighborhoodsScore = neighborhoodMatch * WEIGHTS.neighborhoods;
    score += neighborhoodsScore;
    totalWeight += WEIGHTS.neighborhoods;
    components.neighborhoods = neighborhoodsScore;
  }

  // ─────────────────────────────────────────────
  // Normalize to 0–100
  // ─────────────────────────────────────────────
  const normalizedScore =
    totalWeight > 0 ? (score / totalWeight) * 100 : 0;

  return {
    score: Math.round(normalizedScore),
    rawScore: score,
    totalWeight,
    components,
  };
}

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────

function calculateArrayMatch(arr1: string[], arr2: string[]): number {
  if (!arr1.length || !arr2.length) return 0;

  const set2 = new Set(arr2);
  const intersectionSize = arr1.filter((item) => set2.has(item)).length;

  const union = new Set([...arr1, ...arr2]);
  const unionSize = union.size;

  return unionSize > 0 ? intersectionSize / unionSize : 0;
}

function calculateBudgetOverlapNormalized(
  minA: number,
  maxA: number,
  minB: number,
  maxB: number
): number {
  const overlapMin = Math.max(minA, minB);
  const overlapMax = Math.min(maxA, maxB);
  return Math.max(0, overlapMax - overlapMin);
}

function areCompatible(prefA: string, prefB: string): boolean {
  if (!prefA || !prefB) return false;
  if (prefA === 'negotiable' || prefB === 'negotiable') return true;
  return prefA === prefB;
}

export function getBudgetOverlap(
  userA: User,
  userB: User
): { min: number; max: number } {
  // Normalize inverted budgets
  const aBudgetMin = Math.min(userA.budgetMin, userA.budgetMax);
  const aBudgetMax = Math.max(userA.budgetMin, userA.budgetMax);
  const bBudgetMin = Math.min(userB.budgetMin, userB.budgetMax);
  const bBudgetMax = Math.max(userB.budgetMin, userB.budgetMax);
  
  return {
    min: Math.max(aBudgetMin, bBudgetMin),
    max: Math.min(aBudgetMax, bBudgetMax)
  };
}
